import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CommonList from '../common/components/CommonList';
import { ListSelectionMode } from '../common/components/types';
import { CreateOrganization, EditOrganization, OrganizationFilters } from './components';
import type { Organization } from '../../types/organization';
import { useOrganizationData } from './useOrganizationData';

export default function OrganizationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;

  const { data, pagination, filters, crud, dialogs } = useOrganizationData({ companyId });

  if (data.isFirstLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {data.error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Організації
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Tooltip title="Показати/сховати фільтри">
            <IconButton
              size="small"
              onClick={() => filters.setShowFilters(!filters.showFilters)}
              color={filters.showFilters || filters.hasActiveFilters ? 'primary' : 'default'}
              aria-label="Показати/сховати фільтри"
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          {data.organizations.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => dialogs.setCreateDialogOpen(true)}
            >
              Створити
            </Button>
          )}
        </Box>
      </Box>

      <OrganizationFilters
        show={filters.showFilters}
        filterName={filters.filterName}
        onFilterNameChange={filters.setFilterName}
        showOnlyActive={filters.showOnlyActive}
        onShowOnlyActiveChange={filters.setShowOnlyActive}
        onClearFilters={filters.handleClearFilters}
      />

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ opacity: data.loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {data.organizations.length === 0 && !data.loading ? (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {filters.hasActiveFilters ? (
              <>
                <Typography variant="h6" gutterBottom>
                  За вашим пошуком нічого не знайдено
                </Typography>
                <Button onClick={filters.handleClearFilters} variant="outlined" size="small">
                  Очистити фільтр
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h5" gutterBottom>
                  Організацій ще немає
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Створіть свою першу організацію, щоб почати
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => dialogs.setCreateDialogOpen(true)}
                >
                  Створити
                </Button>
              </>
            )}
          </Box>
        ) : (
          <>
            <CommonList<Organization>
              items={data.organizations}
              toListItem={(item) => ({
                id: item.id,
                primary: item.name,
                archived: !!item.deactivatedDate,
              })}
              onDelete={crud.handleDeleteOrganization}
              onRestore={crud.handleRestoreOrganization}
              onEdit={crud.handleEditClick}
              selectionMode={ListSelectionMode.VIEW}
              showSelection={false}
              emptyMessage="Організації не знайдено"
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                {...pagination}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>

      {companyId && (
        <CreateOrganization
          open={dialogs.createDialogOpen}
          companyId={companyId}
          onClose={() => dialogs.setCreateDialogOpen(false)}
          onSubmit={crud.handleCreateOrganization}
        />
      )}

      <EditOrganization
        open={dialogs.editDialogOpen}
        organization={dialogs.organizationToEdit}
        onClose={() => dialogs.setEditDialogOpen(false)}
        onSubmit={crud.handleUpdateOrganization}
      />
    </Box>
  );
}
